/**
 * Uni Forms — the PUBLIC fill page.
 *
 * This is the page a published form's link opens.  Route:
 * ``/uni-forms/f/:slug`` and ``/uni_forms/f/:slug`` (see ``ROUTES
 * .UNI_FORMS_PUBLIC_FILL`` / ``…_LEGACY``), mounted ABOVE ``RequireAuth``
 * in ``App.jsx``.
 *
 * Why this file exists
 * --------------------
 * ``Form.share_url`` on the backend has always published
 * ``/uni_forms/f/<slug>/``, and the builder's "Copy link" button hands
 * that URL to users.  Nothing rendered it:
 *
 *   * ``uni_forms/urls.py`` mounted only an SPA-shell page, under the
 *     HYPHENATED ``uni-forms/`` prefix — so Django 404'd the link;
 *   * the legacy Django fill view (``uni_forms/views.py::form_fill``) is
 *     not importable in this deployment at all (it reaches
 *     ``accounts.models``, an app this project doesn't have);
 *   * in production nginx sends everything that isn't ``/api/`` ``/admin/``
 *     ``/super_admin/`` ``/django_admin/`` ``/static/`` ``/media/`` to
 *     ``index.html``, and this SPA had no matching route — so the link fell
 *     through to the catch-all and rendered
 *     ``<AccessDenied reason="not-found" />``:
 *     "Page not found / The page you are looking for does not exist."
 *
 * So a correctly published form was unreachable by design.  This page is
 * the missing half.
 *
 * Design decisions worth knowing
 * ------------------------------
 * 1. NO shared ``Uni*`` chrome.  Every other Uni screen renders inside the
 *    authenticated app shell; this one is opened by a stranger, so it is a
 *    standalone document styled from the FORM's own theme (the server
 *    resolves the palette and we set it as ``--uf-*`` custom properties).
 * 2. Anonymous-first.  It uses plain ``axios``, NOT the app's shared
 *    instance: that instance attaches whatever JWT is in localStorage and
 *    redirects to ``/login`` on a 401 — behaviour that is right for the
 *    app and fatal for a public page.  A respondent must never be bounced
 *    to a login screen they have no account for.
 * 3. The server owns validation.  Client-side ``required`` marks are a
 *    convenience; the authoritative errors come back keyed by question id
 *    from ``POST …/submit/`` and are rendered under the offending field.
 * 4. ``state`` drives the render, not HTTP status alone.  The API answers
 *    with ``state: 'open' | 'closed' | 'password_required' |
 *    'login_required' | 'restricted' | 'not_found'`` so this page can say
 *    something true and specific instead of a generic error.
 */
import React from 'react';
import axios from 'axios';
import { useParams, useSearchParams } from 'react-router-dom';

import { APP, ROUTES, STORAGE_KEYS } from '../../config.js';
import { useLiveCalculations } from './useLiveCalculations.js';
import './uniPublicForm.css';


/* Same-origin on purpose: Taskydo is multi-tenant and the tenant is
   resolved from the request Host, so the form must be fetched from the
   host the respondent actually opened.  A hard-coded domain would read
   the wrong schema (or 404). */
function endpoint(slug, action = '') {
  const base = `/api/uni-forms/public/forms/${encodeURIComponent(slug)}/`;
  return action ? `${base}${action}/` : base;
}

function editTokenStorageKey(slug) {
  return `uni-forms-edit-token:${slug}`;
}

function readStoredEditToken(slug) {
  if (!slug) return '';
  try {
    return sessionStorage.getItem(editTokenStorageKey(slug)) || '';
  } catch {
    return '';
  }
}

function storeEditToken(slug, token) {
  if (!slug || !token) return;
  try {
    sessionStorage.setItem(editTokenStorageKey(slug), token);
  } catch {
    /* private mode / blocked storage — the URL param still works */
  }
}

/* A bare axios call — deliberately not ``src/api/client.js``.  See design
   note 2 in the file header: the shared instance would attach a stale
   bearer token and, on 401, navigate the respondent away to /login. */
const http = axios.create({
  withCredentials: true,          // session cookie when present
  headers: { Accept: 'application/json' },
  xsrfCookieName: 'csrftoken',    // Django's names, not axios's defaults
  xsrfHeaderName: 'X-CSRFToken',
  timeout: 30000,
});

/* When a colleague opens a published link while already signed into the
   React app, their JWT lives in localStorage — not in a Django session
   cookie.  Attach it when present so the server can resolve respondent
   identity without turning this page into the authenticated axios client
   (no 401 → /login redirect). */
http.interceptors.request.use((config) => {
  try {
    const access = localStorage.getItem(STORAGE_KEYS.ACCESS);
    if (access) {
      const value = `Bearer ${access}`;
      if (config.headers && typeof config.headers.set === 'function') {
        config.headers.set('Authorization', value);
      } else {
        config.headers = { ...(config.headers || {}), Authorization: value };
      }
    }
  } catch {
    // localStorage unavailable — proceed anonymously.
  }
  return config;
});


function hasStoredAccessToken() {
  try {
    return Boolean(localStorage.getItem(STORAGE_KEYS.ACCESS));
  } catch {
    return false;
  }
}


function viewerFromProfile(me) {
  if (!me || typeof me !== 'object') return null;
  const fullName = me.full_name
    || [me.first_name, me.last_name].filter(Boolean).join(' ').trim()
    || me.email
    || '';
  if (!fullName && !me.email) return null;
  return {
    anonymous: false,
    label: fullName,
    full_name: fullName,
    name: fullName,
    email: me.email || '',
    first_name: me.first_name || '',
    last_name: me.last_name || '',
    city: me.city || '',
    state: me.state || '',
    phone_number: me.phone_number || '',
    username: me.username || '',
    division: me.division_name || '',
    sub_division: me.sub_division_name || '',
    role: me.role_name || '',
    address: me.address || '',
  };
}

function viewerFromStoredUser() {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.USER);
    if (!raw) return null;
    return viewerFromProfile(JSON.parse(raw));
  } catch {
    return null;
  }
}

/** When the form API could not resolve JWT, ask /me/ or use cached login. */
async function enrichViewer(serverViewer) {
  if (serverViewer && !serverViewer.anonymous) return serverViewer;
  try {
    const access = localStorage.getItem(STORAGE_KEYS.ACCESS);
    if (access) {
      const res = await http.get('/api/super_admin/me/');
      const fromMe = viewerFromProfile(res.data);
      if (fromMe) return fromMe;
    }
  } catch {
    // Token may be expired — fall back to the cached login payload.
  }
  return viewerFromStoredUser() || serverViewer;
}


/** Pull the server's ``{state, detail, errors}`` out of an axios error. */
function readError(err) {
  const data = err?.response?.data;
  if (data && typeof data === 'object') return data;
  return {
    detail:
      err?.message === 'Network Error'
        ? "We couldn't reach the server. Check your connection and try again."
        : err?.message || 'Something went wrong.',
  };
}


/** Apply the form's theme as CSS custom properties on the page wrapper. */
function themeStyle(theme) {
  if (!theme) return undefined;
  return {
    '--uf-primary': theme.primary_color || undefined,
    '--uf-bg': theme.bg_color || undefined,
    '--uf-card': theme.card_color || undefined,
    '--uf-text': theme.text_color || undefined,
    '--uf-font': theme.font_family || undefined,
  };
}


/* ====================================================================
   Full-page states
   ==================================================================== */

/**
 * One component for every "we can't show you the questions" outcome.
 *
 * Kept generic because the difference that matters to a respondent is the
 * WORDS, not the layout — and routing all of them through one component
 * guarantees a form's theme still frames the message, so the page never
 * looks like a broken Taskydo error screen.
 */
function StateCard({ icon, title, body, note, bad, children, theme }) {
  return (
    <div className="uf-public" style={themeStyle(theme)}>
      <div className={`uf-state${bad ? ' uf-state--bad' : ''}`}>
        {icon && <div className="uf-state__icon" aria-hidden="true">{icon}</div>}
        <h1 className="uf-state__title">{title}</h1>
        {body && <p className="uf-state__body">{body}</p>}
        {note && <p className="uf-state__note">{note}</p>}
        {children}
      </div>
    </div>
  );
}


/** Password gate.  A separate screen so the questions never render first. */
function PasswordGate({ form, onUnlock, busy, error }) {
  const [value, setValue] = React.useState('');
  return (
    <StateCard
      theme={form?.theme}
      icon="🔒"
      title={form?.title || 'Password required'}
      body="This form is password protected."
    >
      <form
        onSubmit={(e) => { e.preventDefault(); onUnlock(value); }}
        style={{ marginTop: 18 }}
      >
        <input
          type="password"
          className="uf-input"
          value={value}
          autoFocus
          placeholder="Enter password"
          onChange={(e) => setValue(e.target.value)}
        />
        {error && <div className="uf-error">{error}</div>}
        <div style={{ marginTop: 14 }}>
          <button type="submit" className="uf-btn" disabled={busy || !value}>
            {busy ? 'Checking…' : 'Unlock'}
          </button>
        </div>
      </form>
    </StateCard>
  );
}


/** Read-only respondent block — signed-in profile or “Anonymous”. */
function RespondentIdentityCard({ viewer, title = 'Respondent' }) {
  if (!viewer) return null;
  const rows = viewer.anonymous
    ? []
    : [
        ['Name', viewer.full_name || viewer.name],
        ['Email', viewer.email],
        ['City', viewer.city],
        ['Phone', viewer.phone_number],
        ['Division', viewer.division],
        ['Role', viewer.role],
      ].filter(([, value]) => value);

  return (
    <div className="uf-card uf-respondent">
      <div className="uf-q-text">{title}</div>
      {viewer.anonymous ? (
        <p className="uf-respondent__anon" aria-live="polite">Anonymous</p>
      ) : (
        <dl className="uf-respondent__list">
          {rows.map(([label, value]) => (
            <React.Fragment key={label}>
              <dt>{label}</dt>
              <dd>{value}</dd>
            </React.Fragment>
          ))}
        </dl>
      )}
    </div>
  );
}


function identityAnswerFromViewer(viewer) {
  if (!viewer || viewer.anonymous) return 'Anonymous';
  const label = viewer.full_name || viewer.name || viewer.email || 'Anonymous';
  const email = viewer.email || '';
  if (email && email.toLowerCase() !== String(label).toLowerCase()) {
    return `${label} (${email})`;
  }
  return label || 'Anonymous';
}


/* ====================================================================
   Field rendering
   ====================================================================
   One switch, keyed on ``question_type`` (the model has ~35).  Anything
   unrecognised falls back to a single-line text box rather than rendering
   nothing: an unknown type is a *new* type, and silently dropping the
   field would make the form quietly collect less than the author asked
   for.  Layout-only types return null and are handled by the caller.
*/

function Choice({ name, type, option, checked, onChange }) {
  return (
    <label className="uf-choice" htmlFor={`opt-${option.id}`}>
      <input
        id={`opt-${option.id}`}
        type={type}
        name={name}
        value={option.id}
        checked={checked}
        onChange={onChange}
      />
      <span>
        {option.text}
        {option.image && (
          <img className="uf-choice__img" src={option.image} alt="" />
        )}
      </span>
    </label>
  );
}


function AddressFields({ q, value, onPart }) {
  const parts = [
    ['address_line1', 'Address line 1', true],
    ['address_line2', 'Address line 2', true],
    ['city', 'City', false],
    ['state', 'State / region', false],
    ['postal_code', 'Postal code', false],
    ['country', 'Country', false],
  ];
  return (
    <div className="uf-address">
      {parts.map(([key, label, wide]) => (
        <input
          key={key}
          className={`uf-input${wide ? ' uf-address--wide' : ''}`}
          placeholder={label}
          aria-label={`${q.text} — ${label}`}
          value={(value && value[key]) || ''}
          onChange={(e) => onPart(key, e.target.value)}
        />
      ))}
    </div>
  );
}


function captureBrowserLocation() {
  return new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      resolve({ ok: false, error: 'unavailable' });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({
        ok: true,
        latitude: pos.coords.latitude,
        longitude: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
      }),
      (err) => resolve({
        ok: false,
        error: err?.code === 1 ? 'denied' : 'failed',
      }),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 8000 },
    );
  });
}

function hasCoords(value) {
  if (!value || typeof value !== 'object') return false;
  return Number.isFinite(Number(value.latitude))
    && Number.isFinite(Number(value.longitude));
}

function isHiddenLocationQuestion(q) {
  return q?.question_type === 'hidden'
    && (q.hidden_source === 'location_required'
      || q.hidden_source === 'location_optional');
}


function lastValueApplied(current, last) {
  if (last === undefined || last === null) return false;
  try {
    return JSON.stringify(current ?? '') === JSON.stringify(last);
  } catch {
    return current === last;
  }
}


function LastSubmissionHint({ last, current, onUse }) {
  if (!last || !last.display) return null;
  const canUse = last.value !== undefined && last.value !== null && last.value !== '';
  const applied = canUse && lastValueApplied(current, last.value);
  return (
    <div className="uf-last" role="note">
      <span className="uf-last__label">Your last answer</span>
      <span className="uf-last__value">{last.display}</span>
      {canUse && !applied && (
        <button
          type="button"
          className="uf-last__use"
          onClick={() => onUse(last.value)}
        >
          Use this
        </button>
      )}
    </div>
  );
}


/**
 * Geolocation: the address fields PLUS a coordinate capture button.
 *
 * The button is not optional garnish.  The server treats a geolocation
 * answer as blank until it has BOTH a latitude and a longitude (an
 * address alone can be typed from anywhere, which defeats the point of
 * the field), so without a way to capture coordinates a *required*
 * geolocation question would be impossible to satisfy — the respondent
 * would be stuck on "this field is required" with nothing to fill in.
 *
 * ``navigator.geolocation`` needs a secure context, so this degrades to a
 * plain message over http rather than failing silently.
 */
function GeolocationField({ q, value, onPart, onCoords }) {
  const [status, setStatus] = React.useState('');
  const lat = value?.latitude;
  const lng = value?.longitude;
  const autoTried = React.useRef(false);

  const detect = React.useCallback((quiet) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      if (!quiet) setStatus('Location isn’t available in this browser.');
      return;
    }
    if (!quiet) setStatus('Detecting…');
    captureBrowserLocation().then((result) => {
      if (!result.ok) {
        if (!quiet) {
          setStatus(
            result.error === 'denied'
              ? 'Permission denied. Allow location access, or type the address below.'
              : 'We couldn’t detect your location. Please type the address below.',
          );
        }
        return;
      }
      onCoords({
        latitude: result.latitude,
        longitude: result.longitude,
        accuracy: result.accuracy,
      });
      setStatus('');
    });
  }, [onCoords]);

  React.useEffect(() => {
    if (autoTried.current) return undefined;
    if (lat != null && lng != null) return undefined;
    autoTried.current = true;
    detect(true);
    return undefined;
  }, [detect, lat, lng]);

  return (
    <div>
      <div className="uf-actions" style={{ marginBottom: 10 }}>
        <button type="button" className="uf-btn uf-btn--ghost" onClick={() => detect(false)}>
          📍 Use my current location
        </button>
        {lat != null && lng != null && (
          <span className="uf-scale__label">
            {Number(lat).toFixed(5)}, {Number(lng).toFixed(5)}
          </span>
        )}
      </div>
      {status && <div className="uf-q-desc">{status}</div>}
      <AddressFields q={q} value={value} onPart={onPart} />
    </div>
  );
}



/**
 * Render one question's input.
 *
 * ``value`` / ``onChange`` are the whole contract: the parent owns all
 * answers in a single ``{questionId: value}`` map, which is exactly the
 * shape ``POST …/submit/`` expects, so nothing has to be transformed at
 * submit time.  The per-type value shapes are:
 *
 *   text-ish            → string
 *   multiple_choice     → option id (string)
 *   checkbox            → array of option ids
 *   consent             → boolean
 *   address/geolocation → {address_line1, city, …, latitude, longitude}
 *   grids               → {rowLabel: colLabel | [colLabel, …]}
 */
export function Field({ q, value, onChange, invalid }) {
  const cls = `uf-input${invalid ? ' uf-input--invalid' : ''}`;
  const set = (v) => onChange(q.id, v);
  const setPart = (key, v) => onChange(q.id, { ...(value || {}), [key]: v });

  switch (q.question_type) {
    case 'long_text':
      return (
        <textarea
          className={`uf-textarea${invalid ? ' uf-textarea--invalid' : ''}`}
          placeholder={q.placeholder || 'Your answer'}
          value={value || ''}
          onChange={(e) => set(e.target.value)}
        />
      );

    case 'multiple_choice':
      return (
        <div>
          {(q.options || []).map((o) => (
            <Choice
              key={o.id}
              name={`q-${q.id}`}
              type="radio"
              option={o}
              checked={value === o.id}
              onChange={() => set(o.id)}
            />
          ))}
        </div>
      );

    case 'checkbox': {
      const chosen = Array.isArray(value) ? value : [];
      return (
        <div>
          {(q.options || []).map((o) => (
            <Choice
              key={o.id}
              name={`q-${q.id}`}
              type="checkbox"
              option={o}
              checked={chosen.includes(o.id)}
              onChange={() =>
                set(
                  chosen.includes(o.id)
                    ? chosen.filter((id) => id !== o.id)
                    : [...chosen, o.id],
                )
              }
            />
          ))}
        </div>
      );
    }

    case 'dropdown':
      return (
        <select
          className={`uf-select${invalid ? ' uf-select--invalid' : ''}`}
          value={value || ''}
          onChange={(e) => set(e.target.value)}
        >
          <option value="">Choose…</option>
          {(q.options || []).map((o) => (
            <option key={o.id} value={o.id}>{o.text}</option>
          ))}
        </select>
      );

    case 'linear_scale': {
      // Inclusive range — a 1–5 scale must offer 5, not stop at 4.
      const min = Number(q.scale_min ?? 1);
      const max = Number(q.scale_max ?? 5);
      const steps = [];
      for (let i = min; i <= max; i += 1) steps.push(i);
      return (
        <div className="uf-scale">
          {q.scale_min_label && (
            <span className="uf-scale__label">{q.scale_min_label}</span>
          )}
          {steps.map((n) => (
            <label className="uf-choice" key={n}>
              <input
                type="radio"
                name={`q-${q.id}`}
                checked={String(value) === String(n)}
                onChange={() => set(String(n))}
              />
              <span>{n}</span>
            </label>
          ))}
          {q.scale_max_label && (
            <span className="uf-scale__label">{q.scale_max_label}</span>
          )}
        </div>
      );
    }

    case 'rating': {
      const max = Number(q.rating_max || 5);
      const icon = { star: '⭐', heart: '❤️', thumb: '👍', flag: '🚩', smile: '😊' }[
        q.rating_icon
      ] || '⭐';
      const current = Number(value || 0);
      return (
        <div className="uf-rating">
          {Array.from({ length: max }, (_, i) => i + 1).map((n) => (
            <button
              type="button"
              key={n}
              className={`uf-star${n <= current ? ' uf-star--on' : ''}`}
              aria-label={`${n} of ${max}`}
              // Clicking the current value clears it, so a respondent can
              // undo a mis-tap on an optional rating.
              onClick={() => set(n === current ? '' : String(n))}
            >
              {icon}
            </button>
          ))}
        </div>
      );
    }

    case 'slider': {
      const min = Number(q.slider_min ?? 0);
      const max = Number(q.slider_max ?? 100);
      const shown =
        value === '' || value === undefined || value === null
          ? String(q.slider_default ?? min)
          : String(value);
      return (
        <div className="uf-scale">
          <input
            type="range"
            min={min}
            max={max}
            step={Number(q.slider_step || 1)}
            value={shown}
            style={{ flex: 1 }}
            onChange={(e) => set(e.target.value)}
          />
          {q.slider_show_value && <strong>{shown}</strong>}
        </div>
      );
    }

    case 'date':
    case 'time':
      return (
        <input
          type={q.question_type}
          className={cls}
          value={value || ''}
          onChange={(e) => set(e.target.value)}
        />
      );

    case 'consent':
      return (
        <label className="uf-choice">
          <input
            type="checkbox"
            checked={!!value}
            onChange={(e) => set(e.target.checked)}
          />
          {/* ``consent_text`` is author-written and may contain markup, but
              this page is PUBLIC — rendering it as HTML would let a form
              author inject script into a stranger's browser.  Text only. */}
          <span>{q.consent_text || q.text}</span>
        </label>
      );

    case 'address':
      return <AddressFields q={q} value={value} onPart={setPart} />;

    case 'geolocation':
      return (
        <GeolocationField
          q={q}
          value={value}
          onPart={setPart}
          onCoords={(coords) => onChange(q.id, { ...(value || {}), ...coords })}
        />
      );

    case 'multiple_choice_grid':
    case 'checkbox_grid': {
      const multi = q.question_type === 'checkbox_grid';
      const grid = value && typeof value === 'object' ? value : {};
      return (
        <table className="uf-grid">
          <thead>
            <tr>
              <th aria-label="Row" />
              {q.column_labels.map((c) => <th key={c}>{c}</th>)}
            </tr>
          </thead>
          <tbody>
            {q.row_labels.map((r) => (
              <tr key={r}>
                <td>{r}</td>
                {q.column_labels.map((c) => {
                  const cell = grid[r];
                  const on = multi
                    ? Array.isArray(cell) && cell.includes(c)
                    : cell === c;
                  return (
                    <td key={c}>
                      <input
                        type={multi ? 'checkbox' : 'radio'}
                        name={`q-${q.id}-${r}`}
                        aria-label={`${r} — ${c}`}
                        checked={on}
                        onChange={() => {
                          if (!multi) return setPart(r, c);
                          const list = Array.isArray(cell) ? cell : [];
                          return setPart(
                            r,
                            list.includes(c)
                              ? list.filter((x) => x !== c)
                              : [...list, c],
                          );
                        }}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    case 'email':
    case 'email_confirm':
      return (
        <input type="email" className={cls} value={value || ''}
               placeholder={q.placeholder || 'you@example.com'}
               onChange={(e) => set(e.target.value)} />
      );

    case 'phone':
      return (
        <input type="tel" className={cls} value={value || ''}
               placeholder={q.placeholder || 'Phone number'}
               onChange={(e) => set(e.target.value)} />
      );

    case 'number':
      return (
        <input type="number" className={cls} value={value || ''}
               placeholder={q.placeholder || ''}
               onChange={(e) => set(e.target.value)} />
      );

    case 'calculation': {
      const editable = q.calculation_editable === true;
      return (
        <div>
          <input
            type="number"
            className={`${cls}${editable ? '' : ' uf-input--locked'}`}
            value={value || ''}
            placeholder={q.placeholder || ''}
            disabled={!editable}
            readOnly={!editable}
            aria-readonly={!editable}
            onChange={editable ? (e) => set(e.target.value) : undefined}
          />
          <p className="uf-q-hint">
            {editable
              ? 'Calculated from other answers. Changing those answers updates this value; you can then edit it.'
              : 'Calculated from other answers. This field cannot be edited.'}
          </p>
          {q.calculation_reset_active && (
            <p className="uf-q-hint">
              Previous figures in this calculation were reset to 0 for this period. Numbers you enter now still count.
            </p>
          )}
        </div>
      );
    }

    case 'last_answer': {
      const editable = q.last_answer_editable === true;
      return (
        <div>
          <input
            type="text"
            className={`${cls}${editable ? '' : ' uf-input--locked'}`}
            value={value || ''}
            placeholder={q.last_submission?.display ? '' : 'No previous answer yet'}
            disabled={!editable}
            readOnly={!editable}
            aria-readonly={!editable}
            onChange={editable ? (e) => set(e.target.value) : undefined}
          />
          <p className="uf-q-hint">
            {editable
              ? (q.last_submission?.display
                ? 'Started from your last answer to another question on this form. You can change it.'
                : 'You can type an answer. After you submit once, this field will start from your last answer.')
              : (q.last_submission?.display
                ? 'Filled from your last answer to another question on this form.'
                : 'This fills in automatically after you have submitted this form once.')}
          </p>
        </div>
      );
    }

    case 'url':
      return (
        <input type="url" className={cls} value={value || ''}
               placeholder={q.placeholder || 'https://…'}
               onChange={(e) => set(e.target.value)} />
      );

    case 'password':
      return (
        <input type="password" className={cls} value={value || ''}
               placeholder={q.placeholder || ''}
               onChange={(e) => set(e.target.value)} />
      );

    // Layout / content types carry no input — the caller renders their
    // heading or copy and never reaches this component.
    case 'section_header':
    case 'page_break':
    case 'html':
    case 'respondent_identity':
      return null;

    // File upload needs a multipart round-trip and e-signature needs a
    // canvas; neither is wired into this JSON endpoint yet.  Saying so
    // beats rendering a control that silently discards the answer.
    case 'file_upload':
    case 'multi_file':
    case 'signature':
      return (
        <div className="uf-banner uf-banner--warn">
          This field type isn’t supported on the shared link yet. Please ask
          the form owner for another way to send it.
        </div>
      );

    // An unrecognised type is a NEW type (the model has ~35 and grows with
    // each parity phase).  A text box collects something; rendering
    // nothing would make the form quietly ask less than the author wrote.
    case 'short_text':
    default:
      return (
        <input
          type="text"
          className={cls}
          placeholder={q.placeholder || 'Your answer'}
          value={value || ''}
          onChange={(e) => set(e.target.value)}
        />
      );
  }
}


/* ====================================================================
   CAPTCHA — compulsory when platform keys are configured
   ==================================================================== */

function loadExternalScript(src, id) {
  return new Promise((resolve, reject) => {
    if (typeof document === 'undefined') {
      resolve();
      return;
    }
    if (document.getElementById(id)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.id = id;
    script.src = src;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });
}

async function executeRecaptchaV3(siteKey) {
  const src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
  await loadExternalScript(src, 'uf-recaptcha-v3');
  const grecaptcha = window.grecaptcha;
  if (!grecaptcha) {
    throw new Error('reCAPTCHA failed to load. Please refresh and try again.');
  }
  return new Promise((resolve, reject) => {
    grecaptcha.ready(() => {
      grecaptcha.execute(siteKey, { action: 'submit' })
        .then(resolve)
        .catch(reject);
    });
  });
}

/** Invisible honeypot — bots fill it; humans never see it. */
function HoneypotField({ inputRef }) {
  return (
    <input
      ref={inputRef}
      type="text"
      name="website"
      tabIndex={-1}
      autoComplete="off"
      aria-hidden="true"
      className="uf-honeypot"
    />
  );
}

function RecaptchaV2Widget({ captcha, onWidgetId }) {
  const containerRef = React.useRef(null);

  React.useEffect(() => {
    if (!captcha?.enabled || captcha.type !== 'v2' || !captcha.site_key) {
      return undefined;
    }

    let cancelled = false;
    loadExternalScript('https://www.google.com/recaptcha/api.js', 'uf-recaptcha-v2')
      .then(() => {
        if (cancelled || !containerRef.current || !window.grecaptcha) return;
        window.grecaptcha.ready(() => {
          if (cancelled || !containerRef.current) return;
          const widgetId = window.grecaptcha.render(containerRef.current, {
            sitekey: captcha.site_key,
          });
          onWidgetId(widgetId);
        });
      })
      .catch(() => {
        /* submit handler surfaces a generic captcha error */
      });

    return () => { cancelled = true; };
  }, [captcha, onWidgetId]);

  if (!captcha?.enabled || captcha.type !== 'v2') return null;
  return <div ref={containerRef} className="uf-recaptcha" />;
}

function TurnstileWidget({ captcha, onToken }) {
  const containerRef = React.useRef(null);

  React.useEffect(() => {
    if (!captcha?.enabled || captcha.type !== 'turnstile' || !captcha.site_key) {
      return undefined;
    }

    let cancelled = false;
    loadExternalScript(
      'https://challenges.cloudflare.com/turnstile/v0/api.js',
      'uf-turnstile',
    )
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        window.turnstile.render(containerRef.current, {
          sitekey: captcha.site_key,
          callback: (token) => onToken(token),
          'expired-callback': () => onToken(''),
          'error-callback': () => onToken(''),
        });
      })
      .catch(() => {
        /* submit handler surfaces a generic captcha error */
      });

    return () => { cancelled = true; };
  }, [captcha, onToken]);

  if (!captcha?.enabled || captcha.type !== 'turnstile') return null;
  return <div ref={containerRef} className="uf-turnstile" />;
}

function CaptchaBlock({ captcha, honeypotRef, onV2WidgetId, onTurnstileToken }) {
  if (!captcha?.enabled) return null;

  return (
  <div className="uf-captcha">
    <HoneypotField inputRef={honeypotRef} />
    {captcha.type === 'honeypot' ? null : (
      <>
    {captcha.type === 'v2' && (
      <RecaptchaV2Widget captcha={captcha} onWidgetId={onV2WidgetId} />
    )}
    {captcha.type === 'turnstile' && (
      <TurnstileWidget captcha={captcha} onToken={onTurnstileToken} />
    )}
    {captcha.type === 'v3' && (
      <p className="uf-captcha__note">
        This form is protected by reCAPTCHA.
      </p>
    )}
      </>
    )}
  </div>
  );
}


/* ====================================================================
   The page
   ==================================================================== */

export default function UniFormsPublicFill() {
  const { slug } = useParams();
  const [search] = useSearchParams();

  const [form, setForm]       = React.useState(null);
  const [phase, setPhase]     = React.useState('loading');
  const [gate, setGate]       = React.useState(null);   // {state, detail, …}
  const [answers, setAnswers] = React.useState({});
  const [email, setEmail]     = React.useState('');
  const [errors, setErrors]   = React.useState({});
  const [banner, setBanner]   = React.useState('');
  const [busy, setBusy]       = React.useState(false);
  const [pwError, setPwError] = React.useState('');
  const [done, setDone]       = React.useState(null);
  // A landing page (when the author enabled one) is shown before the
  // questions.  ``?start=1`` skips it, matching the legacy view's contract
  // so links that already carry the parameter behave the same.
  const [started, setStarted] = React.useState(search.get('start') === '1');
  const [v2WidgetId, setV2WidgetId] = React.useState(null);
  const [turnstileToken, setTurnstileToken] = React.useState('');
  const honeypotRef = React.useRef(null);
  const [locStatus, setLocStatus] = React.useState('');

  /** Seed answers from URL params, signed-in profile, then ``default_value``. */
  const seedAnswers = React.useCallback((questions, viewer) => {
    const seeded = {};
    (questions || []).forEach((q) => {
      if (q.question_type === 'respondent_identity') {
        seeded[q.id] = identityAnswerFromViewer(viewer);
        return;
      }
      if (isHiddenLocationQuestion(q)) return;
      if (q.question_type === 'hidden'
          && ['ip', 'user_agent', 'timestamp'].includes(q.hidden_source)) {
        return;
      }
      if (q.prefill_param) {
        const fromUrl = search.get(q.prefill_param);
        if (fromUrl !== null && fromUrl !== '') {
          seeded[q.id] = fromUrl;
          return;
        }
      }
      if (q.question_type === 'last_answer' && q.last_submission) {
        const last = q.last_submission;
        if (last.display) {
          seeded[q.id] = last.display;
          return;
        }
        if (last.value !== undefined && last.value !== null && last.value !== '') {
          seeded[q.id] = last.value;
          return;
        }
      }
      if (
        q.prefill_last_submission
        && q.last_submission
        && q.last_submission.value !== undefined
        && q.last_submission.value !== null
        && q.last_submission.value !== ''
      ) {
        seeded[q.id] = q.last_submission.value;
        return;
      }
      if (q.prefill_param) {
        const fromProfile = viewer?.[q.prefill_param];
        if (fromProfile !== undefined && fromProfile !== null && fromProfile !== '') {
          seeded[q.id] = String(fromProfile);
          return;
        }
        if (
          viewer?.anonymous
          && ['full_name', 'name', 'label'].includes(q.prefill_param)
        ) {
          seeded[q.id] = 'Anonymous';
          return;
        }
      }
      if (q.default_value) seeded[q.id] = q.default_value;
    });
    return seeded;
  }, [search]);

  const adopt = React.useCallback((payload) => {
    setForm(payload);
    setGate(payload.state === 'open' ? null : payload);
    setPhase(payload.state === 'open' ? 'ready' : payload.state);
    if (payload.state === 'open') {
      const seeded = seedAnswers(payload.questions, payload.viewer);
      if (payload.existing_answers && typeof payload.existing_answers === 'object') {
        Object.assign(seeded, payload.existing_answers);
      }
      setAnswers(seeded);
      if (payload.collect_email) {
        setEmail(payload.respondent_email || payload.viewer?.email || '');
      }
      if (payload.edit_token) storeEditToken(slug, payload.edit_token);
      if (payload.editing) setStarted(true);
    }
  }, [seedAnswers, slug]);

  React.useEffect(() => {
    let cancelled = false;
    setPhase('loading');
    const params = {};
    const urlToken = search.get('edit_token') || '';
    const wantEdit = search.get('edit') === '1' || !!urlToken;
    if (wantEdit) {
      const token = urlToken || readStoredEditToken(slug);
      if (token) params.edit_token = token;
      params.edit = '1';
    }
    http.get(endpoint(slug), { params })
      .then(async (res) => {
        if (cancelled) return;
        const data = { ...res.data };
        if (data.state === 'open') {
          data.viewer = await enrichViewer(data.viewer);
        }
        adopt(data);
      })
      .catch((err) => {
        if (cancelled) return;
        const data = readError(err);
        setGate(data);
        setForm((prev) => prev || (data.theme ? data : null));
        setPhase(data.state || 'error');
      });
    return () => { cancelled = true; };
  }, [slug, adopt, search]);

  const setAnswer = React.useCallback((qid, value) => {
    setAnswers((prev) => ({ ...prev, [qid]: value }));
    // Clear this field's server error as soon as it's touched — leaving a
    // red message under a field the respondent has just corrected reads as
    // "still wrong" and is the fastest way to make a form feel broken.
    setErrors((prev) => {
      if (!prev[qid]) return prev;
      const next = { ...prev };
      delete next[qid];
      return next;
    });
  }, []);

  useLiveCalculations(
    phase === 'ready' ? (form?.questions || []) : [],
    answers,
    setAnswers,
    { recomputeOnMount: true },
  );

  const applyLocationToHidden = React.useCallback((questions, coords) => {
    (questions || []).forEach((q) => {
      if (isHiddenLocationQuestion(q)) {
        setAnswer(q.id, coords);
      }
    });
  }, [setAnswer]);

  React.useEffect(() => {
    if (phase !== 'ready' || !form) return undefined;
    const locQs = (form.questions || []).filter(isHiddenLocationQuestion);
    if (!locQs.length) return undefined;
    let cancelled = false;
    setLocStatus('detecting');
    captureBrowserLocation().then((result) => {
      if (cancelled) return;
      if (result.ok) {
        applyLocationToHidden(locQs, {
          latitude: result.latitude,
          longitude: result.longitude,
          accuracy: result.accuracy,
        });
        setLocStatus('ok');
      } else {
        setLocStatus(result.error || 'failed');
      }
    });
    return () => { cancelled = true; };
  }, [phase, form, applyLocationToHidden]);

  const unlock = (password) => {
    setBusy(true);
    setPwError('');
    const urlToken = search.get('edit_token') || '';
    const wantEdit = search.get('edit') === '1' || !!urlToken;
    const token = wantEdit ? (urlToken || readStoredEditToken(slug)) : '';
    http.post(endpoint(slug), {
      password,
      ...(token ? { edit_token: token } : {}),
      ...(wantEdit ? { edit: true } : {}),
    })
      .then(async (res) => {
        const data = { ...res.data };
        if (data.state === 'open') {
          data.viewer = await enrichViewer(data.viewer);
        }
        adopt(data);
      })
      .catch((err) => setPwError(readError(err).detail || 'Incorrect password.'))
      .finally(() => setBusy(false));
  };

  const resetCaptcha = React.useCallback(() => {
    if (v2WidgetId !== null && window.grecaptcha) {
      window.grecaptcha.reset(v2WidgetId);
    }
    setTurnstileToken('');
  }, [v2WidgetId]);

  const buildCaptchaPayload = async () => {
    const captcha = form?.captcha;
    if (!captcha?.enabled) return {};

    const payload = { website: honeypotRef.current?.value || '' };

    if (captcha.type === 'honeypot') {
      return payload;
    }
    if (captcha.type === 'v3') {
      const token = await executeRecaptchaV3(captcha.site_key);
      return { ...payload, 'g-recaptcha-response': token };
    }
    if (captcha.type === 'v2') {
      const token = v2WidgetId !== null
        ? window.grecaptcha?.getResponse(v2WidgetId)
        : '';
      if (!token) {
        throw new Error('Please complete the CAPTCHA challenge before submitting.');
      }
      return { ...payload, 'g-recaptcha-response': token };
    }
    if (captcha.type === 'turnstile') {
      if (!turnstileToken) {
        throw new Error('Please complete the CAPTCHA challenge before submitting.');
      }
      return { ...payload, 'cf-turnstile-response': turnstileToken };
    }
    return payload;
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setBanner('');
    setErrors({});
    try {
      const questions = form?.questions || [];
      let payloadAnswers = { ...answers };
      const requiredLoc = questions.filter(
        (q) => q.question_type === 'hidden'
          && q.hidden_source === 'location_required',
      );
      const missing = requiredLoc.filter((q) => !hasCoords(payloadAnswers[q.id]));
      if (missing.length) {
        setLocStatus('detecting');
        const result = await captureBrowserLocation();
        if (!result.ok) {
          setLocStatus(result.error || 'failed');
          setBanner(
            'This form needs your device location. Allow location access and try again.',
          );
          setBusy(false);
          return;
        }
        const coords = {
          latitude: result.latitude,
          longitude: result.longitude,
          accuracy: result.accuracy,
        };
        missing.forEach((q) => {
          payloadAnswers[q.id] = coords;
        });
        applyLocationToHidden(questions, coords);
        setLocStatus('ok');
      }
      const optionalLoc = questions.filter(
        (q) => q.question_type === 'hidden'
          && q.hidden_source === 'location_optional'
          && !hasCoords(payloadAnswers[q.id]),
      );
      if (optionalLoc.length && locStatus !== 'ok') {
        const result = await captureBrowserLocation();
        if (result.ok) {
          const coords = {
            latitude: result.latitude,
            longitude: result.longitude,
            accuracy: result.accuracy,
          };
          optionalLoc.forEach((q) => {
            payloadAnswers[q.id] = coords;
          });
          applyLocationToHidden(questions, coords);
          setLocStatus('ok');
        }
      }
      const captchaPayload = await buildCaptchaPayload();
      const editToken = form.editing
        ? (form.edit_token || readStoredEditToken(slug))
        : '';
      const res = await http.post(endpoint(slug, 'submit'), {
        answers: payloadAnswers,
        respondent_email: email,
        ...(editToken ? { edit_token: editToken } : {}),
        ...(form.editing ? { edit: true } : {}),
        ...captchaPayload,
      });
      if (res.data?.edit_token) storeEditToken(slug, res.data.edit_token);
      setDone(res.data);
      setPhase('submitted');
      if (typeof window !== 'undefined') window.scrollTo(0, 0);
    } catch (err) {
      const data = readError(err);
      if (err?.message && !data.detail && !data.errors) {
        setBanner(err.message);
        resetCaptcha();
      } else {
        setErrors(data.errors || {});
        setBanner(data.detail || 'We could not record your response.');
        if (data.state === 'captcha_failed') {
          resetCaptcha();
        }
        if (data.state && data.state !== 'submitted' && !data.errors) {
          setGate(data);
          setPhase(data.state);
        }
      }
    } finally {
      setBusy(false);
    }
  };

  const theme = form?.theme;

  // ---- Non-form states -------------------------------------------------
  if (phase === 'loading') {
    return (
      <StateCard theme={theme} title="Loading form…">
        <div className="uf-spinner" role="status" aria-label="Loading" />
      </StateCard>
    );
  }

  if (phase === 'submitted' && done) {
    return <SubmittedCard done={done} theme={theme} slug={slug} />;
  }

  if (phase === 'password_required') {
    return (
      <PasswordGate form={form} onUnlock={unlock} busy={busy} error={pwError} />
    );
  }

  if (phase === 'login_required') {
    return (
      <StateCard
        theme={theme}
        icon="🔐"
        title={gate?.title || 'Sign in to continue'}
        body={gate?.detail}
      >
        <div className="uf-state__actions">
          {/* ``next`` so the respondent lands back on THIS form after
              signing in, rather than on the app dashboard — otherwise they
              have to dig the link out of their inbox again. */}
          <a
            className="uf-btn"
            href={`/login?next=${encodeURIComponent(
              typeof window !== 'undefined'
                ? window.location.pathname + window.location.search
                : '',
            )}`}
          >
            Sign in
          </a>
        </div>
      </StateCard>
    );
  }

  if (phase === 'restricted') {
    return (
      <StateCard
        bad
        theme={theme}
        icon="🔒"
        title={gate?.title || 'Restricted form'}
        body={gate?.detail}
        note={
          gate?.internal_notice
          || 'If you should have access, ask an administrator to add you to '
             + "the form's audience."
        }
      />
    );
  }

  if (phase === 'closed') {
    return (
      <StateCard
        bad
        theme={theme}
        icon="🚫"
        title={form?.title || 'Form closed'}
        body="This form is no longer accepting responses."
        note="If you believe this is a mistake, please contact the form owner."
      />
    );
  }

  if (phase === 'not_found' || phase === 'error' || !form) {
    return (
      <StateCard
        bad
        theme={theme}
        icon="🧭"
        title="We couldn’t open this form"
        body={gate?.detail
          || 'The link may be incomplete, or the form may have been removed.'}
        note="Double-check the link, or ask whoever shared it to send it again."
      />
    );
  }

  // ---- The author's landing page (optional, shown before questions) ----
  if (form.landing_page_enabled && !started) {
    return (
      <div className="uf-public" style={themeStyle(theme)}>
        {form.custom_css && <style>{form.custom_css}</style>}
        <div className="uf-container">
          <div className="uf-card uf-card--header">
            {/* Author-written HTML.  Trusted here for the same reason the
                legacy template trusted it: only a form owner (an
                authenticated tenant user) can write it, so the builder is
                where that trust boundary already sits.  Question-level text
                stays escaped — see the ``consent`` case above. */}
            <div
              className="uf-authored"
              dangerouslySetInnerHTML={{ __html: form.landing_page_html }}
            />
            <div className="uf-actions" style={{ marginTop: 18 }}>
              <button
                type="button"
                className="uf-btn"
                onClick={() => setStarted(true)}
              >
                {form.landing_page_button_text || 'Start'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---- The form itself -------------------------------------------------
  const questions = form.questions || [];
  const answerable = questions.filter(
    (q) => !['section_header', 'page_break', 'html', 'respondent_identity',
             'hidden']
      .includes(q.question_type),
  );
  const hasIdentityQuestion = questions.some(
    (q) => q.question_type === 'respondent_identity',
  );
  const filled = answerable.filter((q) => {
    const v = answers[q.id];
    if (Array.isArray(v)) return v.length > 0;
    if (v && typeof v === 'object') return Object.values(v).some(Boolean);
    return v !== undefined && v !== '' && v !== false;
  }).length;
  const progress = answerable.length
    ? Math.round((filled / answerable.length) * 100)
    : 0;

  return (
    <div className="uf-public" style={themeStyle(theme)}>
      {/* The author's "Custom Styles".  Scoped by convention to
          ``.uniforms-public-form``; we add that class on the wrapper below
          so rules written for the legacy page keep applying. */}
      {form.custom_css && <style>{form.custom_css}</style>}

      <div className="uf-container uniforms-public-form">
        <div className="uf-card uf-card--header">
          {theme?.header_image && (
            <img className="uf-header-image" src={theme.header_image} alt="" />
          )}
          <h1 className="uf-title">{form.title}</h1>
          {form.description && <p className="uf-desc">{form.description}</p>}
          {form.editing && (
            <p className="uf-desc">
              You are editing your submitted response. Save to replace the
              answers already recorded.
            </p>
          )}
        </div>

        {form.show_progress_bar && (
          <div
            className="uf-progress"
            role="progressbar"
            aria-valuenow={progress}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div className="uf-progress__fill" style={{ width: `${progress}%` }} />
          </div>
        )}

        {banner && <div className="uf-banner uf-banner--error">{banner}</div>}

        {questions.some(isHiddenLocationQuestion) && (
          <div
            className={
              `uf-capture${
                locStatus === 'denied' || locStatus === 'failed'
                || locStatus === 'unavailable'
                  ? questions.some((q) => q.hidden_source === 'location_required')
                    ? ' uf-capture--bad'
                    : ' uf-capture--warn'
                  : ''
              }`
            }
            role="status"
          >
            {locStatus === 'detecting' && 'Detecting your location…'}
            {locStatus === 'ok' && 'Your device location will be submitted with this form.'}
            {(locStatus === 'denied' || locStatus === 'failed' || locStatus === 'unavailable') && (
              questions.some((q) => q.hidden_source === 'location_required')
                ? 'This form needs your device location. Allow location access, then submit again.'
                : 'Location could not be read. You can still submit this form.'
            )}
            {!locStatus && 'This form records your device location when you submit.'}
          </div>
        )}

        <form onSubmit={submit} noValidate>
          {form.show_respondent_identity !== false
            && !hasIdentityQuestion
            && form.viewer && (
            <RespondentIdentityCard viewer={form.viewer} />
          )}

          {form.collect_email && (
            <div className="uf-card">
              <div className="uf-q-text">
                Email address<span className="uf-req">*</span>
              </div>
              <input
                type="email"
                className={`uf-input${errors.respondent_email ? ' uf-input--invalid' : ''}`}
                placeholder="you@example.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setErrors((p) => {
                    if (!p.respondent_email) return p;
                    const n = { ...p };
                    delete n.respondent_email;
                    return n;
                  });
                }}
              />
              {errors.respondent_email && (
                <div className="uf-error">{errors.respondent_email}</div>
              )}
            </div>
          )}

          {questions.map((q) => {
            // Page breaks exist only to paginate the legacy template's one
            // long <form>.  This page renders a single continuous list, so
            // there is nothing to draw for them — skipping beats an empty
            // card that reads as a rendering glitch.
            if (q.question_type === 'page_break') return null;

            if (q.question_type === 'section_header') {
              return (
                <div className="uf-card" key={q.id}>
                  <h2 className="uf-section-title">{q.text}</h2>
                  {q.description && <p className="uf-q-desc">{q.description}</p>}
                </div>
              );
            }

            if (q.question_type === 'html') {
              return (
                <div className="uf-card" key={q.id}>
                  {/* Author-written content block — same trust boundary as
                      the landing page above. */}
                  <div
                    className="uf-authored"
                    dangerouslySetInnerHTML={{ __html: q.html_content || '' }}
                  />
                </div>
              );
            }

            // A hidden field passes data through the URL; it asks nothing,
            // so rendering it would only expose plumbing.
            if (q.question_type === 'hidden') return null;

            if (q.question_type === 'respondent_identity') {
              return (
                <RespondentIdentityCard
                  key={q.id}
                  viewer={form.viewer}
                  title={q.text || 'Respondent'}
                />
              );
            }

            const invalid = !!errors[q.id];
            return (
              <div className="uf-card" key={q.id}>
                <div className="uf-q-text">
                  {q.text}
                  {q.is_required && <span className="uf-req">*</span>}
                </div>
                {q.description && <p className="uf-q-desc">{q.description}</p>}
                {q.image && <img className="uf-q-image" src={q.image} alt="" />}
                {q.show_last_submission && q.question_type !== 'last_answer' && (
                  <LastSubmissionHint
                    last={q.last_submission}
                    current={answers[q.id]}
                    onUse={(value) => setAnswer(q.id, value)}
                  />
                )}
                <Field
                  q={q}
                  value={answers[q.id]}
                  onChange={setAnswer}
                  invalid={invalid}
                />
                {invalid && <div className="uf-error">{errors[q.id]}</div>}
              </div>
            );
          })}

          <div className="uf-card">
            <CaptchaBlock
              captcha={form.captcha}
              honeypotRef={honeypotRef}
              onV2WidgetId={setV2WidgetId}
              onTurnstileToken={setTurnstileToken}
            />
            <div className="uf-actions">
              <button type="submit" className="uf-btn" disabled={busy}>
                {busy
                  ? (form.editing
                    ? 'Saving…'
                    : (form.submit_loading_text || 'Submitting…'))
                  : (form.editing
                    ? 'Save changes'
                    : (form.submit_button_text || 'Submit'))}
              </button>
              {Object.keys(errors).length > 0 && (
                <span className="uf-error" style={{ marginTop: 0 }}>
                  Please review the highlighted answers.
                </span>
              )}
            </div>
          </div>
        </form>
      </div>

      <div className="uf-footer">
        Never submit passwords or payment details through this form.
        {' '}Powered by {APP.name}.
      </div>
    </div>
  );
}


/* ====================================================================
   Success screen
   ==================================================================== */

/**
 * Shown after a successful submit.
 *
 * The post-submit redirect is handled here rather than server-side because
 * there is no page navigation to hang a ``<meta refresh>`` on — the SPA
 * never left.  The author's countdown is honoured, and cancellable, so a
 * respondent who wants to read the confirmation isn't yanked away
 * mid-sentence.
 */
function SubmittedCard({ done, theme, slug }) {
  const [left, setLeft] = React.useState(
    Number(done.redirect_delay_seconds || 0),
  );
  const [cancelled, setCancelled] = React.useState(false);
  const url = done.redirect_url || '';
  const newTab = !!done.redirect_open_new_tab;

  React.useEffect(() => {
    if (done.edit_token) storeEditToken(slug, done.edit_token);
  }, [done.edit_token, slug]);

  React.useEffect(() => {
    if (!url || cancelled) return undefined;
    if (newTab) {
      // A new tab is a courtesy — if the browser blocks the popup the
      // respondent still has the link rendered below.
      try {
        window.open(url, '_blank', 'noopener,noreferrer');
      } catch {
        /* popup blocked — the inline link covers it */
      }
      return undefined;
    }
    if (left <= 0) {
      window.location.replace(url);
      return undefined;
    }
    const timer = setTimeout(() => setLeft((n) => n - 1), 1000);
    return () => clearTimeout(timer);
  }, [url, newTab, left, cancelled]);

  return (
    <div className="uf-public" style={themeStyle(theme)}>
      <div className="uf-state">
        {done.completion_html ? (
          <div
            className="uf-authored"
            dangerouslySetInnerHTML={{ __html: done.completion_html }}
          />
        ) : (
          <>
            <div className="uf-state__icon" aria-hidden="true">✅</div>
            <h1 className="uf-state__title">{done.title}</h1>
            <p className="uf-state__body">{done.confirmation_message}</p>
          </>
        )}

        {typeof done.total_score === 'number' && (
          <div className="uf-quiz-score">
            <div>Your score</div>
            <div className="uf-quiz-score__value">
              {done.total_score} / {done.max_possible_score}
            </div>
          </div>
        )}

        {/* Figures pulled from other modules are best-effort: the response
            is already saved, so a resolution failure is a note, not an
            error.  Surfacing it means an admin hears about it from the
            person who hit it, instead of finding a blank cell in a report
            weeks later. */}
        {Array.isArray(done.figure_warnings) && done.figure_warnings.length > 0 && (
          <div className="uf-banner uf-banner--warn" style={{ marginTop: 16 }}>
            {done.figure_warnings.join(' ')}
          </div>
        )}

        {url && !newTab && !cancelled && (
          <div className="uf-state__note">
            Redirecting you in {Math.max(left, 0)}s …{' '}
            <a href={url}>Go now</a>
            {' · '}
            <button
              type="button"
              className="uf-btn uf-btn--ghost"
              style={{ padding: '2px 10px' }}
              onClick={() => setCancelled(true)}
            >
              Stay here
            </button>
          </div>
        )}
        {url && newTab && (
          <div className="uf-state__note">
            Your next step opened in a new tab. If it didn’t,{' '}
            <a href={url} target="_blank" rel="noopener noreferrer">
              open it here
            </a>.
          </div>
        )}

        <div className="uf-state__actions">
          {done.allow_edit_response && (
            <a
              className="uf-btn"
              href={
                `/uni-forms/f/${slug}?edit=1&start=1`
                + (done.edit_token
                  ? `&edit_token=${encodeURIComponent(done.edit_token)}`
                  : '')
              }
            >
              Edit your response
            </a>
          )}
          {done.form && hasStoredAccessToken() && (
            <a
              className="uf-btn"
              href={ROUTES.UNI_FORMS_FORM_RESPONSES(done.form) + '?self=1'}
            >
              View my response
            </a>
          )}
          {/* A full reload, not a router link: the page holds the previous
              answers in state and re-mounting is the simplest way to give
              the next respondent a genuinely blank form. */}
          {!done.limit_one_response && (
            <a className="uf-btn uf-btn--ghost" href={`/uni-forms/f/${slug}`}>
              Submit another response
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

